import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Cell,
  CellChange,
  Id,
  ReactGrid,
  ReactGridProps,
  Row,
} from '@silevis/reactgrid';
import { useLocalStorage } from 'usehooks-ts';
import './style.css';
import {
  getColumnsForUploadMode,
  getGroupsForUploadMode,
} from '../helpers/columns-v2';
import { UploadMode } from 'further-types/investor-investment-upload';
import { InvestmentPaymentType } from 'further-types/payment';
import {
  applyChangesToPrevRows,
  buildRowsFromGridData,
  createInitialGridData,
  getEmptyRow,
  GRID_DATA_STORAGE_KEY,
  GridData,
  GridRow,
  GridCellValue,
  isRowFullyPaidByCashBalance,
} from '../helpers/gridHelpersV2';
import CustomDropdownTemplate from './CustomDropdownTemplateV2';
import CustomNumberCellTemplateV2 from './CustomNumberCellTemplateV2';
import CustomSortcodeTemplate from '../ReactGridForm/CustomSortcodeTemplate';
import CustomDateTemplate from '../ReactGridForm/CustomDateTemplate';
import ActionButtons from '../ReactGridForm/ActionButtons';
import ErrorsTable from './ErrorsTableV2';
import useInvestmentUploadV2 from 'hooks/data/investment/useInvestmentUploadV2';
import { useNotification } from 'hooks/ui/useNotification';
import { PaymentEmailStatus } from 'further-types/investment';
import { AccountType } from 'further-types/investor';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { ApiRouter } from 'further-api/trpc/routes';
import {
  buildValidateAndPreviewInput,
  getValidationIssues,
  toCalendarDay,
} from '../helpers/mapUploadGridDataToV2';
import { fromPairs, groupBy, map, uniqBy } from 'lodash';
import type {
  IdentityLookupInput,
  IdentityLookupResult,
  UploadPreviewState,
} from '../types';
import {
  IdentifyUploadInvestorMatchStatus,
  UploadIssue,
} from 'further-types/investor-investment-upload';

const ACCOUNT_TYPES: ReadonlySet<string> = new Set(Object.values(AccountType));

const isAccountType = (value?: string): value is AccountType =>
  typeof value === 'string' && ACCOUNT_TYPES.has(value);

const toTrimmedString = (value: GridCellValue) => {
  if (typeof value !== 'string') return undefined;
  return value.trim() || undefined;
};

const toNumberValue = (value: GridCellValue) => {
  if (value === '' || value == null) return undefined;

  const num = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(num) ? num : undefined;
};

const parseRowId = (rowId: Id): number | null => {
  if (typeof rowId === 'number') return rowId;
  const parsed = parseInt(String(rowId), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const isUseCashBalanceEnabled = (value: GridCellValue) => value === 'true';

const UNPAID_PAYMENT_EMAIL_STATUSES: ReadonlySet<PaymentEmailStatus> = new Set([
  PaymentEmailStatus.UnpaidEmail,
  PaymentEmailStatus.UnpaidPaymentReminderEmail,
  PaymentEmailStatus.UnpaidNoEmail,
  PaymentEmailStatus.UnpaidDisableEmail,
]);

const isUnpaidPaymentEmailStatus = (
  value: GridCellValue,
): value is PaymentEmailStatus =>
  typeof value === 'string' &&
  UNPAID_PAYMENT_EMAIL_STATUSES.has(value as PaymentEmailStatus);

const ScrollableTable = styled('div')({
  overflow: 'auto',
  padding: '10px 0 25px',
  margin: '0 0 10px',
  clear: 'both',
});

const TableHeaderGroup = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  flexDirection: 'row',
  flexWrap: 'nowrap',
});

const Loader = styled(Box)({
  width: '100%',
  height: '50vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StyledButton = styled(Button)(({ theme }) => ({
  display: 'block',
  marginLeft: 'auto',
  marginTop: theme.spacing(5),
}));

type StyledGroupProps = {
  totalSize: number;
  index: number;
};

const StyledGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'totalSize' && prop !== 'index',
})<StyledGroupProps>(({ index, totalSize }) => ({
  marginLeft: index === 1 ? 50 : 0,
  width: totalSize,
  backgroundColor: index % 2 === 0 ? '#cccccc' : '#dddddd',
  padding: '2px 5px',
}));

type Props = {
  gridDropdownsData: Record<
    string,
    undefined | Array<{ label: string; value: string }>
  >;
  uploadPreviewSet: (state: UploadPreviewState) => void;
  firmId: string;
  uploadMode?: UploadMode;
};

type GridHighlightError = UploadIssue & {
  borderColor: string;
};

type DisplayIssue = UploadIssue & {
  displayRowId: number;
};

const FIRM_SPECIFIC_COLUMN_IDS = [
  'tranche',
  'adviser',
  'additional_linked_adviser',
  'paid_to',
  'new_investor_adviser',
  'new_investor_legal_entity_adviser',
];

const ReactGridFormV2: React.FC<Props> = ({
  gridDropdownsData,
  uploadPreviewSet,
  firmId,
  uploadMode = UploadMode.Full,
}) => {
  const hasNormalisedCacheRef = useRef(false);
  const prevFirmIdRef = useRef<string | undefined>(undefined);
  const [cols, setCols] = useState(() => getColumnsForUploadMode(uploadMode));
  const [gridData, setGridData] = useLocalStorage<GridData>(
    GRID_DATA_STORAGE_KEY,
    createInitialGridData(),
  );
  const gridDataRef = useRef<GridData>(gridData);
  const lastValidateDataRef = useRef<typeof validate.data | null>(null);
  const [highlightErrors, setHighlightErrors] = useState<
    Array<GridHighlightError>
  >([]);
  const [displayErrors, setDisplayErrors] = useState<Array<DisplayIssue>>([]);
  const [dropdownsData, setDropdownsData] = useState(gridDropdownsData);
  const [dobMismatchErrors, setDobMismatchErrors] = useState<
    Array<DisplayIssue>
  >([]);
  const [warningIssues, setWarningIssues] = useState<Array<DisplayIssue>>([]);
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const { validate, identityLookup } = useInvestmentUploadV2();
  const { error: showError } = useNotification();
  useEffect(() => {
    gridDataRef.current = gridData;
  }, [gridData]);
  const lastIdentityLookupRef = useRef<Map<number, string>>(new Map());
  const pendingIdentityLookupRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    setCols(getColumnsForUploadMode(uploadMode));
  }, [uploadMode]);

  useEffect(() => {
    if (
      prevFirmIdRef.current !== undefined &&
      prevFirmIdRef.current !== firmId
    ) {
      setGridData((prev) =>
        prev.map((row) => {
          const next = { ...row };
          FIRM_SPECIFIC_COLUMN_IDS.forEach((columnId) => {
            next[columnId] = '';
          });
          return next;
        }),
      );
    }
    prevFirmIdRef.current = firmId;
  }, [firmId, setGridData]);

  const modeGroups = useMemo(
    () => getGroupsForUploadMode(uploadMode),
    [uploadMode],
  );

  const columnLabelById = useMemo(() => {
    const labels: Record<string, string> = {};
    cols.forEach((column) => {
      labels[column.columnId] = column.header;
    });
    return labels;
  }, [cols]);

  const toDisplayIssue = (issue: UploadIssue) => {
    const label = columnLabelById[issue.columnId];
    const message = label ? `${label}: ${issue.message}` : issue.message;
    return {
      ...issue,
      message,
      displayRowId: issue.rowId,
    };
  };

  const toHighlightIssue = (issue: UploadIssue) => ({
    ...issue,
    rowId: Math.max(issue.rowId - 1, 0),
    borderColor: '#ff8888',
  });

  useEffect(() => {
    setDropdownsData(gridDropdownsData);
  }, [gridDropdownsData]);

  const extractIdentityFields = (rowData: GridRow) => {
    const accountTypeValue = toTrimmedString(rowData.account_type);
    const firstName = toTrimmedString(rowData.first_name);
    const lastOrCorporateName = toTrimmedString(rowData.last_or_corporate_name);
    const identityDate = toCalendarDay(rowData.identity_date);

    const inferredAccountType = firstName
      ? AccountType.Individual
      : AccountType.LegalEntity;
    const accountType = accountTypeValue || inferredAccountType;

    if (!isAccountType(accountType)) return null;
    if (!identityDate || !lastOrCorporateName) return null;
    if (accountType === AccountType.Individual && !firstName) return null;

    return { accountType, firstName, lastOrCorporateName, identityDate };
  };

  const buildIdentityLookupInput = (
    rowId: number,
    rowData: GridRow,
  ): IdentityLookupInput | null => {
    const identity = extractIdentityFields(rowData);
    if (!identity) return null;

    return {
      rowId,
      ...identity,
      investmentAmount: toNumberValue(rowData.investment_amount),
      adviserFee: toNumberValue(rowData.advice_fee),
      useCashBalance: isUseCashBalanceEnabled(rowData.use_cash_balance),
      firmId,
    };
  };

  const buildIdentitySignature = (input: IdentityLookupInput) => {
    // Cache key includes identity + useCashBalance + investmentAmount + adviserFee so we re-lookup when these change (API returns capped cash balance when amount is set)
    return [
      input.accountType,
      input.firstName ?? '',
      input.lastOrCorporateName ?? '',
      input.identityDate?.toString() ?? '',
      input.useCashBalance ? 'cash' : 'no-cash',
      String(input.investmentAmount ?? ''),
      String(input.adviserFee ?? ''),
    ].join('|');
  };

  const applyIdentityLookupResult = (result: IdentityLookupResult) => {
    if (result.status !== IdentifyUploadInvestorMatchStatus.Existing) return;

    setGridData((currentGridData) => {
      const targetRow = currentGridData[result.rowId];
      if (!targetRow) return currentGridData;

      const nextRow = { ...targetRow };

      // Apply adviser if missing
      if (!nextRow.adviser && result.defaultAdviserId) {
        const match = dropdownsData.adviser?.find(
          (a) => a.value === result.defaultAdviserId,
        );
        if (match) nextRow.adviser = match.value;
      }

      // Apply cash balance when enabled and API returned a value (so it updates when investment amount is set later and API returns capped value)
      if (
        result.cashBalance !== undefined &&
        isUseCashBalanceEnabled(nextRow.use_cash_balance)
      ) {
        nextRow.cash_balance = result.cashBalance;
      }

      // When row becomes fully paid by cash balance, set paid_to; keep payment status only if already a paid option
      if (isRowFullyPaidByCashBalance(nextRow)) {
        nextRow.paid_to = InvestmentPaymentType.CashBalance;
        if (isUnpaidPaymentEmailStatus(nextRow.payment_email_status)) {
          nextRow.payment_email_status = '';
        }
      }

      return currentGridData.map((row, idx) =>
        idx === result.rowId ? nextRow : row,
      );
    });
  };

  const triggerIdentityLookup = (rowId: number, rowData: GridRow) => {
    const input = buildIdentityLookupInput(rowId, rowData);
    if (!input) return;
    const signature = buildIdentitySignature(input);

    if (pendingIdentityLookupRef.current.get(rowId) === signature) return;
    if (lastIdentityLookupRef.current.get(rowId) === signature) return;

    pendingIdentityLookupRef.current.set(rowId, signature);

    identityLookup.mutate(input, {
      onSuccess: (result) => {
        const pendingSignature = pendingIdentityLookupRef.current.get(rowId);
        if (pendingSignature !== signature) return;
        pendingIdentityLookupRef.current.delete(rowId);
        lastIdentityLookupRef.current.set(rowId, signature);
        applyIdentityLookupResult(result);
      },
      onError: () => {
        const pendingSignature = pendingIdentityLookupRef.current.get(rowId);
        if (pendingSignature === signature) {
          pendingIdentityLookupRef.current.delete(rowId);
        }
      },
    });
  };

  useEffect(
    function investorsValidated() {
      if (validate.data) {
        if (lastValidateDataRef.current === validate.data) return;
        lastValidateDataRef.current = validate.data;
        const { overrideData, previewData, issues } = validate.data;

        if (overrideData?.length) {
          setGridData((gridData) => {
            const overridesByRowIndex = groupBy(
              overrideData,
              (o) => o.rowId - 1,
            );
            return gridData.map((row, index) => {
              const overrides = overridesByRowIndex[index];
              if (!overrides?.length) return row;
              const patch = fromPairs(
                map(overrides, (o) => [o.columnId, o.value]),
              );
              return { ...row, ...patch };
            });
          });
        }

        const emailDobMismatchErrors = issues.filter(
          (issue) => issue.code === 'EmailDobMismatch',
        );
        const fieldErrors = issues.filter(
          (issue) =>
            issue.severity === 'Error' && issue.code !== 'EmailDobMismatch',
        );
        const warningIssuesList = issues.filter(
          (issue) => issue.severity === 'Warning',
        );

        setWarningIssues(
          (warningIssuesList || []).map((issue) => toDisplayIssue(issue)),
        );

        if (
          fieldErrors.length ||
          emailDobMismatchErrors.length ||
          (!proceedAnyway && warningIssuesList.length)
        ) {
          setHighlightErrors(
            fieldErrors.map((issue) => toHighlightIssue(issue)),
          );
          setDisplayErrors(fieldErrors.map((issue) => toDisplayIssue(issue)));
          setDobMismatchErrors(
            (emailDobMismatchErrors || []).map((issue) =>
              toDisplayIssue(issue),
            ),
          );
          return;
        }

        setHighlightErrors([]);
        setDisplayErrors([]);
        setDobMismatchErrors([]);
        setWarningIssues([]);
        setProceedAnyway(false);

        const { newInvestors, existingInvestors } = previewData;
        const currentGridData = gridDataRef.current;

        const uniqueNewInvestors = uniqBy(newInvestors, 'email');
        const uniqueExistingInvestors = uniqBy(existingInvestors, 'investorId');

        const uploadPreview: UploadPreviewState = {
          totalInvestors:
            uniqueNewInvestors.length + uniqueExistingInvestors.length,
          existingInvestorAccounts: [],
        };

        if (uniqueExistingInvestors.length) {
          uploadPreview.existingInvestorAccounts = uniqueExistingInvestors.map(
            (investor) => {
              const investorData = currentGridData.find(
                (row, index) =>
                  Number(row.index ?? index + 1) === investor.rowId,
              );
              const baseRow = investorData ?? getEmptyRow(0);
              return {
                ...baseRow,
                fullName: investor.displayName,
                email: investor.email,
              };
            },
          );
        }
        uploadPreviewSet(uploadPreview);
      }
    },
    [validate.data, proceedAnyway, uploadPreviewSet],
  );

  useEffect(() => {
    if (hasNormalisedCacheRef.current) return;
    hasNormalisedCacheRef.current = true;

    if (!gridData.length) {
      setGridData(createInitialGridData());
      return;
    }

    const hasMissingData = cols.some((column) =>
      gridData.some(
        (cachedRow) => !Object.keys(cachedRow).includes(column.columnId),
      ),
    );
    if (hasMissingData) {
      setGridData(createInitialGridData());
    }
  }, [cols, gridData, setGridData]);

  /**
   * Cell change event handler
   * @see https://reactgrid.com/docs/4.0/7-api/1-types/2-cell-change/
   * @param {Array} changes
   */
  const handleChanges = (changes: Array<CellChange>) => {
    const newGridData = applyChangesToPrevRows(changes, gridData);
    const affectedRowIds = new Set(
      changes
        .map(({ rowId }) => parseRowId(rowId))
        .filter((id): id is number => id !== null),
    );

    // Clear cash_balance if use_cash_balance is disabled
    affectedRowIds.forEach((rowId) => {
      const row = newGridData[rowId];
      if (
        row &&
        !isUseCashBalanceEnabled(row.use_cash_balance) &&
        row.cash_balance !== 0
      ) {
        row.cash_balance = '';
      }
    });

    // Only update paid_to and payment_email_status when the row crosses the "fully paid by cash balance" boundary.
    affectedRowIds.forEach((rowId) => {
      const row = newGridData[rowId];
      if (!row) {
        return;
      }

      const prevRow = gridData[rowId];
      const wasFullyPaidByCashBalance = prevRow
        ? isRowFullyPaidByCashBalance(prevRow)
        : false;
      const isFullyPaidByCashBalance = isRowFullyPaidByCashBalance(row);

      if (wasFullyPaidByCashBalance !== isFullyPaidByCashBalance) {
        if (isFullyPaidByCashBalance) {
          row.paid_to = InvestmentPaymentType.CashBalance;
          if (isUnpaidPaymentEmailStatus(row.payment_email_status)) {
            row.payment_email_status = '';
          }
        } else {
          row.paid_to = '';
          row.payment_email_status = '';
        }
      }
    });

    // Auto-populate payment date from investment date when investment date changes
    changes.forEach(({ rowId, columnId }) => {
      if (columnId !== 'investment_date') {
        return;
      }

      const id = parseRowId(rowId);

      if (id === null || !newGridData[id]) {
        return;
      }

      const investmentDate = newGridData[id].investment_date;
      const currentPaymentDate = newGridData[id].payment_date;
      const isEmpty = currentPaymentDate == null || currentPaymentDate === '';
      const prevRow = gridData[id];
      const previousInvestmentDate =
        prevRow != null ? prevRow.investment_date : null;
      const paymentDateWasSyncedToInvestment =
        previousInvestmentDate != null &&
        previousInvestmentDate !== '' &&
        String(currentPaymentDate) === String(previousInvestmentDate);

      if (investmentDate != null && investmentDate !== '') {
        // Only set if the payment date was empty as to avoid overwriting the payment date if it was already set:
        if (isEmpty) {
          newGridData[id].payment_date = investmentDate;
        } else if (paymentDateWasSyncedToInvestment) {
          // If the payment date was synced to the investment date, update it to the new investment date
          newGridData[id].payment_date = investmentDate;
        }
        // Otherwise, leave the payment date untouched
      }
    });

    setGridData(newGridData);
    setProceedAnyway(false);

    // Trigger identity lookup for affected rows
    affectedRowIds.forEach((rowId) => {
      const row = newGridData[rowId];
      if (row) triggerIdentityLookup(rowId, row);
    });
  };

  const handleValidateAndPreview = () => {
    const allFormatIssues = getValidationIssues(gridData);

    if (allFormatIssues.length) {
      setHighlightErrors(allFormatIssues.map(toHighlightIssue));
      setDisplayErrors(allFormatIssues.map(toDisplayIssue));
      return;
    }

    validate.mutate(
      buildValidateAndPreviewInput(gridData, dropdownsData, firmId, uploadMode),
      {
        onError: (err: TRPCClientErrorLike<ApiRouter>) => {
          const message =
            err?.data?.cause ?? err?.message ?? 'Validation failed.';
          showError(message);
        },
      },
    );
  };

  const handleAddRow = (count = 1) => {
    // add rows as many as count
    if (gridData.length + count > 350) {
      return;
    }
    const newRows = Array.from({ length: count }, (_, k) =>
      getEmptyRow(gridData.length + k),
    );
    setGridData((prevGridData) => [...prevGridData, ...newRows]);
    setProceedAnyway(false);
  };

  const handleRemoveRow = (count = 1) => {
    // if there are no rows, don't do anything
    // if there are fewer rows than count, remove all the rows except the first one
    if (gridData.length <= 1) {
      return;
    }
    if (gridData.length < count) {
      count = gridData.length - 1;
    }
    // remove errors from the removed rows
    setHighlightErrors((prevErrors) => [
      ...prevErrors.filter((error) => error.rowId < gridData.length - count),
    ]);
    setDisplayErrors((prevErrors) => [
      ...prevErrors.filter(
        (error) => (error.displayRowId ?? 0) < gridData.length - count,
      ),
    ]);
    // add rows as many as count
    const newGridData = [...gridData.slice(0, -1 * count)];
    setGridData(newGridData);
    setProceedAnyway(false);
  };

  const handleColumnResize = (ci: Id, width: number) =>
    setCols((prevColumns) => {
      const columnIndex = prevColumns.findIndex(
        (el) => el.columnId === String(ci),
      );
      const resizedColumn = prevColumns[columnIndex];
      prevColumns[columnIndex] = { ...resizedColumn, width };
      return [...prevColumns];
    });

  const columnGroups = useMemo(
    () =>
      modeGroups.map((group) => ({
        groupId: group.groupId,
        title: group.title,
        totalSize: group.columns.reduce((acc, col) => {
          const newCol = cols.find((c) => c.columnId === col.columnId);
          return acc + (newCol?.width ?? 0);
        }, 0),
      })),
    [cols, modeGroups],
  );

  const { columnGroup, columnGroupWidth } = useMemo(() => {
    let columnGroupWidth = 0;
    const colGroups = columnGroups.map((group, index) => {
      columnGroupWidth += group.totalSize;
      if (index === 0) {
        return null;
      }

      return (
        <StyledGroup
          key={group.title}
          className="group"
          totalSize={group.totalSize}
          index={index}
        >
          {group.title}
        </StyledGroup>
      );
    });

    return { columnGroup: colGroups, columnGroupWidth };
  }, [columnGroups]);

  if (!dropdownsData) return <Loader>Preparing an upload table...</Loader>;

  const rows: Array<Row<Cell>> = buildRowsFromGridData(
    gridData,
    dropdownsData,
    cols,
  );

  const customCellTemplates: ReactGridProps['customCellTemplates'] = {
    number: new CustomNumberCellTemplateV2(),
    custom_dropdown: new CustomDropdownTemplate(),
    custom_sortcode: new CustomSortcodeTemplate({}),
    custom_date: new CustomDateTemplate({}),
  };

  return (
    <>
      <div className="d-flex justify-content-between">
        <ScrollableTable>
          <TableHeaderGroup style={{ width: columnGroupWidth }}>
            {columnGroup}
          </TableHeaderGroup>
          <ReactGrid
            rows={rows}
            columns={cols}
            onCellsChanged={handleChanges}
            onColumnResized={handleColumnResize}
            enableRangeSelection
            enableRowSelection
            enableColumnSelection
            highlights={highlightErrors}
            stickyLeftColumns={1}
            stickyTopRows={1}
            customCellTemplates={customCellTemplates}
          />
        </ScrollableTable>
        <ActionButtons
          gridDataLength={gridData.length || 0}
          handleAddRow={handleAddRow}
          handleRemoveRow={handleRemoveRow}
          handleValidateAndPreview={handleValidateAndPreview}
          loadingUploadPreview={validate.isPending}
        />
      </div>
      <div className="scrollable-table errors-table">
        {displayErrors.length > 0 && <ErrorsTable errors={displayErrors} />}
      </div>
      <div className="scrollable-table errors-table">
        {dobMismatchErrors.length > 0 && (
          <ErrorsTable
            errors={dobMismatchErrors}
            title="Investor details error"
            description="The following investor accounts are unable to be created as the email address matches an investor with an existing Further account, but the identity date is different."
          />
        )}
      </div>
      <div className="scrollable-table errors-table">
        {warningIssues.length > 0 && (
          <>
            <ErrorsTable
              errors={warningIssues}
              title="Warnings"
              description="The following rows contain warnings. Please see below for more information or proceed anyway."
            />
            {!highlightErrors.length && (
              <StyledButton
                variant="contained"
                color="primary"
                onClick={() => {
                  setProceedAnyway(true);
                  handleValidateAndPreview();
                }}
              >
                Proceed anyway
              </StyledButton>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ReactGridFormV2;
